import { createTheme, ThemeOptions } from '@mui/material/styles';
import React from 'react';
import {
  ButtonProps,
  CircularProgress,
  GridProps,
  InputAdornment,
  InputBaseProps,
  outlinedInputClasses,
  styled,
} from '@mui/material';
import { SidebarSiteConfigItem } from 'store/types/SiteConfig';
import SiteModule from 'store/enums/SiteModule';

export const defaultGridItemProps: GridProps = { item: true, xs: 12 };
export const defaultGridContainerProps: GridProps = { container: true, spacing: 3 };

export const materialIconsClassName = 'material-icons-outlined';

export const getInputLoadingProps = (loading?: boolean): InputBaseProps => ({
  startAdornment: loading ? (
    <InputAdornment position={'start'}>
      <CircularProgress size={20} color={'inherit'} />
    </InputAdornment>
  ) : null,
});

export const getButtonLoadingProps = (loading?: boolean): ButtonProps =>
  loading
    ? {
        startIcon: <CircularProgress color={'inherit'} size={16} />,
      }
    : {};

export const appTheme: Partial<ThemeOptions> = {
  typography: {
    fontSize: 16,
    //fontFamily: ['Montserrat', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'].join(','),
    h1: {
      fontFamily: 'Poppins',
      // fontStyle: "normal",
      fontWeight: 600,
      fontSize: '28px',
      lineHeight: '32px',
      margin: '0.67em 0',
    },
    h2: {
      fontFamily: 'Poppins',
      // fontStyle: "normal",
      fontWeight: 500,
      fontSize: '24px',
      lineHeight: '29px',
    },
    h5: {
      fontFamily: 'Poppins',
      // fontStyle: "normal",
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '21px',
    },
    h6: {
      fontFamily: 'Poppins',
      // fontStyle: "normal",
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '21px',
    },
    body1: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: '#080C12',
    },
    body2: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '20px',
      color: '#6A6A6A',
    },
    button: {
      textTransform: 'none',
      fontFamily: 'Poppins',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 769,
      md: 960,
      lg: 1340,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: '#172535',
    },
    secondary: {
      main: '#3F5E9D',
    },
    text: {
      primary: '#717273',
    },
  },
  components: {
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          //'border': '1px solid #CAD2E6',
          borderRadius: '5px',
          //'height': '44px'
          // '--TextField-brandBorderColor': '#FFFFFF',
          // '--TextField-brandBorderHoverColor': '#B2BAC2',
          // '--TextField-brandBorderFocusedColor': '#6F7E8C',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // notchedOutline: {
        //   borderColor: 'var(--TextField-brandBorderColor)',
        // },
        root: {
          //'height': '44px',
          // [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
          //   borderColor: 'var(--TextField-brandBorderHoverColor)',
          // },
          // [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          //   borderColor: 'var(--TextField-brandBorderFocusedColor)',
          // },
        },
      },
    },
  },
};

export const nccerTheme = createTheme({
  ...appTheme,
  // typography: {
  //   ...appTheme.typography,
  //   fontFamily: ['Noto Sans'].join(','),
  // },
  palette: {
    primary: {
      main: '#172535',
    },
    secondary: {
      main: '#3F5E9D',
    },
    text: {
      primary: '#717273',
    },
  },
  // components: {
  //   MuiTextField: {
  //     styleOverrides: {
  //       root: {
  //         '--TextField-brandBorderColor': '#FFFFFF',
  //         '--TextField-brandBorderHoverColor': '#B2BAC2',
  //         '--TextField-brandBorderFocusedColor': '#6F7E8C',
  //       }
  //     }
  //   }
  // }
});

interface FaviconLinkConfigItem {
  href: string;
  rel?: string;
  type?: string;
  sizes?: string;
}

const defaultLinksConfig: Array<FaviconLinkConfigItem> = [
  {
    rel: 'shortcut icon',
    type: 'image/png',
    href: '/images/default/favicons/favicon.ico',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '57x57',
    href: '/images/default/favicons/apple-icon-57x57.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '60x60',
    href: '/images/default/favicons/apple-icon-60x60.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '72x72',
    href: '/images/default/favicons/apple-icon-72x72.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '76x76',
    href: '/images/default/favicons/apple-icon-76x76.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '114x114',
    href: '/images/default/favicons/apple-icon-114x114.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '120x120',
    href: '/images/default/favicons/apple-icon-120x120.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '144x144',
    href: '/images/default/favicons/apple-icon-144x144.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '152x152',
    href: '/images/default/favicons/apple-icon-152x152.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/images/default/favicons/apple-icon-180x180.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '192x192',
    href: '/images/default/favicons/android-icon-192x192.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: '/images/default/favicons/favicon-32x32.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '96x96',
    href: '/images/default/favicons/favicon-96x96.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: '/images/default/favicons/favicon-16x16.png',
  },
];

export const addAppFavicons = (): void => {
  defaultLinksConfig.forEach(({ href = '', type = '', sizes = '', rel = '' }) => {
    const newLink = document.createElement('link');

    newLink.href = href.replace('default', 'nccer');
    newLink.rel = rel;

    if (sizes) {
      newLink.setAttribute('sizes', sizes);
    }
    if (type) {
      newLink.setAttribute('type', type);
    }
    document.head.appendChild(newLink);
  });
};

export const getPageTitle = (
  sidebar: SidebarSiteConfigItem[] = [],
  moduleName: SiteModule,
  subModuleName?: SiteModule
): string => {
  const foundSidebarConfig = sidebar.find(({ type }) => type === moduleName);
  if (subModuleName && foundSidebarConfig) {
    const children = foundSidebarConfig.children?.find(({ type }) => type === subModuleName);

    return `${children && children.title} ${foundSidebarConfig.title}`;
  }
  return foundSidebarConfig ? foundSidebarConfig.title : '';
};

export const getIconSvg = (iconName?: string, newActive?: string) => {
  if (iconName !== '' && iconName?.localeCompare(newActive ?? '', undefined, { sensitivity: 'base' }) == 0) {
    iconName += '_selected';
  }
  switch (iconName) {
    case 'home':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M9.02 2.84L3.63 7.04C2.73 7.74 2 9.23 2 10.36V17.77C2 20.09 3.89 21.99 6.21 21.99H17.79C20.11 21.99 22 20.09 22 17.78V10.5C22 9.29 21.19 7.74 20.2 7.05L14.02 2.72C12.62 1.74 10.37 1.79 9.02 2.84Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12 17.99V14.99" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'home_selected':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M9.02 2.84004L3.63 7.04004C2.73 7.74004 2 9.23004 2 10.36V17.77C2 20.09 3.89 21.99 6.21 21.99H17.79C20.11 21.99 22 20.09 22 17.78V10.5C22 9.29004 21.19 7.74004 20.2 7.05004L14.02 2.72004C12.62 1.74004 10.37 1.79004 9.02 2.84004Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12 17.99V14.99" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'checkmark':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10.7896 15.1698C10.5896 15.1698 10.3996 15.0898 10.2596 14.9498L7.83957 12.5298C7.54957 12.2398 7.54957 11.7598 7.83957 11.4698C8.12957 11.1798 8.60957 11.1798 8.89957 11.4698L10.7896 13.3598L15.0896 9.05979C15.3796 8.76979 15.8596 8.76979 16.1496 9.05979C16.4396 9.34979 16.4396 9.82978 16.1496 10.1198L11.3196 14.9498C11.1796 15.0898 10.9896 15.1698 10.7896 15.1698Z"
            fill="white"
          />
          <path
            d="M11.9999 22.75C11.3699 22.75 10.7399 22.54 10.2499 22.12L8.66988 20.76C8.50988 20.62 8.10988 20.48 7.89988 20.48H6.17988C4.69988 20.48 3.49988 19.28 3.49988 17.8V16.09C3.49988 15.88 3.35988 15.49 3.21988 15.33L1.86988 13.74C1.04988 12.77 1.04988 11.24 1.86988 10.27L3.21988 8.68C3.35988 8.52 3.49988 8.13 3.49988 7.92V6.2C3.49988 4.72 4.69988 3.52 6.17988 3.52H7.90988C8.11988 3.52 8.51988 3.37 8.67988 3.24L10.2599 1.88C11.2399 1.04 12.7699 1.04 13.7499 1.88L15.3299 3.24C15.4899 3.38 15.8899 3.52 16.0999 3.52H17.7999C19.2799 3.52 20.4799 4.72 20.4799 6.2V7.9C20.4799 8.11 20.6299 8.51 20.7699 8.67L22.1299 10.25C22.9699 11.23 22.9699 12.76 22.1299 13.74L20.7699 15.32C20.6299 15.48 20.4799 15.88 20.4799 16.09V17.79C20.4799 19.27 19.2799 20.47 17.7999 20.47H16.0999C15.8899 20.47 15.4899 20.62 15.3299 20.75L13.7499 22.11C13.2599 22.54 12.6299 22.75 11.9999 22.75ZM6.17988 5.02C5.52988 5.02 4.99988 5.55 4.99988 6.2V7.91C4.99988 8.48 4.72988 9.21 4.35988 9.64L3.00988 11.23C2.65988 11.64 2.65988 12.35 3.00988 12.76L4.35988 14.35C4.72988 14.79 4.99988 15.51 4.99988 16.08V17.79C4.99988 18.44 5.52988 18.97 6.17988 18.97H7.90988C8.48988 18.97 9.21988 19.24 9.65988 19.62L11.2399 20.98C11.6499 21.33 12.3699 21.33 12.7799 20.98L14.3599 19.62C14.7999 19.25 15.5299 18.97 16.1099 18.97H17.8099C18.4599 18.97 18.9899 18.44 18.9899 17.79V16.09C18.9899 15.51 19.2599 14.78 19.6399 14.34L20.9999 12.76C21.3499 12.35 21.3499 11.63 20.9999 11.22L19.6399 9.64C19.2599 9.2 18.9899 8.47 18.9899 7.89V6.2C18.9899 5.55 18.4599 5.02 17.8099 5.02H16.1099C15.5299 5.02 14.7999 4.75 14.3599 4.37L12.7799 3.01C12.3699 2.66 11.6499 2.66 11.2399 3.01L9.65988 4.38C9.21988 4.75 8.47988 5.02 7.90988 5.02H6.17988Z"
            fill="white"
          />
        </svg>
      );
    case 'checkmark_selected':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10.75 2.45001C11.44 1.86001 12.57 1.86001 13.27 2.45001L14.85 3.81001C15.15 4.07001 15.71 4.28001 16.11 4.28001H17.81C18.87 4.28001 19.74 5.15001 19.74 6.21001V7.91001C19.74 8.30001 19.95 8.87001 20.21 9.17001L21.57 10.75C22.16 11.44 22.16 12.57 21.57 13.27L20.21 14.85C19.95 15.15 19.74 15.71 19.74 16.11V17.81C19.74 18.87 18.87 19.74 17.81 19.74H16.11C15.72 19.74 15.15 19.95 14.85 20.21L13.27 21.57C12.58 22.16 11.45 22.16 10.75 21.57L9.17 20.21C8.87 19.95 8.31 19.74 7.91 19.74H6.18C5.12 19.74 4.25 18.87 4.25 17.81V16.1C4.25 15.71 4.04 15.15 3.79 14.85L2.44 13.26C1.86 12.57 1.86 11.45 2.44 10.76L3.79 9.17001C4.04 8.87001 4.25 8.31001 4.25 7.92001V6.20001C4.25 5.14001 5.12 4.27001 6.18 4.27001H7.91C8.3 4.27001 8.87 4.06001 9.17 3.80001L10.75 2.45001Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.37988 12L10.7899 14.42L15.6199 9.57996"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'chart':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z"
            fill="white"
          />
          <path
            d="M5.59998 19.7499H4C3.04 19.7499 2.25 18.9599 2.25 17.9999V9.37988C2.25 8.41988 3.04 7.62988 4 7.62988H5.59998C6.55998 7.62988 7.34998 8.41988 7.34998 9.37988V17.9999C7.34998 18.9599 6.55998 19.7499 5.59998 19.7499ZM4 9.11987C3.86 9.11987 3.75 9.22987 3.75 9.36987V17.9999C3.75 18.1399 3.86 18.2499 4 18.2499H5.59998C5.73998 18.2499 5.84998 18.1399 5.84998 17.9999V9.37988C5.84998 9.23988 5.73998 9.12988 5.59998 9.12988H4V9.11987Z"
            fill="white"
          />
          <path
            d="M12.8002 19.7499H11.2002C10.2402 19.7499 9.4502 18.9599 9.4502 17.9999V6.18994C9.4502 5.22994 10.2402 4.43994 11.2002 4.43994H12.8002C13.7602 4.43994 14.5502 5.22994 14.5502 6.18994V17.9999C14.5502 18.9599 13.7602 19.7499 12.8002 19.7499ZM11.2002 5.93994C11.0602 5.93994 10.9502 6.04994 10.9502 6.18994V17.9999C10.9502 18.1399 11.0602 18.2499 11.2002 18.2499H12.8002C12.9402 18.2499 13.0502 18.1399 13.0502 17.9999V6.18994C13.0502 6.04994 12.9402 5.93994 12.8002 5.93994H11.2002Z"
            fill="white"
          />
          <path
            d="M20.0004 19.75H18.4004C17.4404 19.75 16.6504 18.96 16.6504 18V3C16.6504 2.04 17.4404 1.25 18.4004 1.25H20.0004C20.9604 1.25 21.7504 2.04 21.7504 3V18C21.7504 18.96 20.9604 19.75 20.0004 19.75ZM18.4004 2.75C18.2604 2.75 18.1504 2.86 18.1504 3V18C18.1504 18.14 18.2604 18.25 18.4004 18.25H20.0004C20.1404 18.25 20.2504 18.14 20.2504 18V3C20.2504 2.86 20.1404 2.75 20.0004 2.75H18.4004Z"
            fill="white"
          />
        </svg>
      );
    case 'chart_selected':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 22H21" stroke="#172535" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M5.59998 8.38H4C3.45 8.38 3 8.83 3 9.38V18C3 18.55 3.45 19 4 19H5.59998C6.14998 19 6.59998 18.55 6.59998 18V9.38C6.59998 8.83 6.14998 8.38 5.59998 8.38Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.7999 5.19H11.2C10.65 5.19 10.2 5.64 10.2 6.19V18C10.2 18.55 10.65 19 11.2 19H12.7999C13.3499 19 13.7999 18.55 13.7999 18V6.19C13.7999 5.64 13.3499 5.19 12.7999 5.19Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20 2H18.4C17.85 2 17.4 2.45 17.4 3V18C17.4 18.55 17.85 19 18.4 19H20C20.55 19 21 18.55 21 18V3C21 2.45 20.55 2 20 2Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'settings':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 15.25C10.2061 15.25 8.75 13.7939 8.75 12C8.75 10.2061 10.2061 8.75 12 8.75C13.7939 8.75 15.25 10.2061 15.25 12C15.25 13.7939 13.7939 15.25 12 15.25ZM12 9.25C10.4839 9.25 9.25 10.4839 9.25 12C9.25 13.5161 10.4839 14.75 12 14.75C13.5161 14.75 14.75 13.5161 14.75 12C14.75 10.4839 13.5161 9.25 12 9.25Z"
            stroke="white"
          />
          <path
            d="M9.29506 21.6257L9.29505 21.6256L9.28974 21.6271C8.78935 21.7621 8.27942 21.6904 7.84725 21.4311L7.84729 21.431L7.83943 21.4265L6.11942 20.4365L6.11883 20.4361C5.62587 20.1533 5.26895 19.6936 5.11296 19.1303C4.96627 18.5647 5.04116 17.991 5.32369 17.4986L5.32465 17.4969C5.48974 17.2066 5.60884 16.9073 5.656 16.6176C5.70286 16.3298 5.68369 16.0139 5.52135 15.7369C5.21133 15.2081 4.53815 15.0298 3.9 15.0298C2.71614 15.0298 1.75 14.0637 1.75 12.8798V11.1198C1.75 9.93594 2.71614 8.9698 3.9 8.9698C4.53815 8.9698 5.21133 8.79152 5.52135 8.26266C5.8349 7.72778 5.64497 7.0482 5.32247 6.49885C5.04025 6.00567 4.96658 5.42221 5.11336 4.86774L5.11381 4.86601C5.25952 4.30744 5.61339 3.84883 6.1158 3.56521L6.1158 3.56522L6.11834 3.56377L7.84834 2.57377L7.84837 2.57382L7.85501 2.56988C8.74208 2.04392 9.92719 2.34696 10.4696 3.26429L10.4696 3.26429L10.4713 3.26705L10.5893 3.46373C10.92 4.03392 11.4019 4.4748 12.015 4.4748C12.6294 4.4748 13.1119 4.03215 13.4427 3.46032C13.4427 3.46026 13.4428 3.4602 13.4428 3.46015L13.5513 3.27266C13.5516 3.27228 13.5518 3.27191 13.552 3.27154C14.0945 2.34756 15.277 2.04423 16.1739 2.57921L16.1738 2.57926L16.1806 2.58314L17.9006 3.57314L17.9012 3.57348C18.3941 3.85633 18.7511 4.31601 18.907 4.87927C19.0537 5.44489 18.9788 6.01857 18.6963 6.51096L18.6954 6.51265C18.5303 6.80299 18.4112 7.10226 18.364 7.39196C18.3171 7.67978 18.3363 7.99572 18.4987 8.27266C18.8087 8.80152 19.4819 8.9798 20.12 8.9798C21.3039 8.9798 22.27 9.94594 22.27 11.1298V12.8898C22.27 14.0737 21.3039 15.0398 20.12 15.0398C19.4818 15.0398 18.8087 15.2181 18.4987 15.7469C18.1851 16.2818 18.375 16.9614 18.6975 17.5108C18.9824 18.0084 19.0608 18.5898 18.9084 19.1352L18.9084 19.1352L18.9062 19.1436C18.7605 19.7022 18.4066 20.1608 17.9042 20.4444L17.9017 20.4458L16.1751 21.4339C15.8657 21.6042 15.5395 21.6898 15.21 21.6898C15.05 21.6898 14.8823 21.6673 14.7042 21.6254C14.2097 21.4877 13.8 21.1779 13.5387 20.7425L13.4207 20.5458C13.0899 19.9756 12.6081 19.5348 11.995 19.5348C11.3806 19.5348 10.898 19.9775 10.5672 20.5494L10.4592 20.736C10.4589 20.7365 10.4586 20.737 10.4583 20.7375C10.1931 21.1867 9.7813 21.5002 9.29506 21.6257ZM13.8567 20.2893L13.8573 20.2903L13.9659 20.4779C13.9661 20.4783 13.9664 20.4788 13.9667 20.4792C14.1561 20.8099 14.4704 21.0428 14.8387 21.1349C15.1931 21.2235 15.5696 21.1836 15.8933 20.9909L17.6199 19.9929C17.9901 19.7793 18.2775 19.4223 18.3931 18.9886C18.5063 18.5642 18.451 18.1176 18.2331 17.7399L18.2322 17.7384C17.7242 16.865 17.7069 16.0504 18.0341 15.4779C18.3586 14.91 19.0688 14.5198 20.09 14.5198C21.0061 14.5198 21.74 13.7859 21.74 12.8698V11.1098C21.74 10.2065 21.009 9.4598 20.09 9.4598C19.0688 9.4598 18.3586 9.06961 18.0341 8.50173C17.7069 7.92916 17.7242 7.11458 18.2322 6.24119L18.2331 6.23966C18.451 5.86198 18.5063 5.41541 18.3931 4.99097C18.2776 4.55795 18.0025 4.21577 17.642 3.99397L17.6353 3.98979L17.6283 3.98583L15.903 2.9985C15.2278 2.59456 14.3551 2.83599 13.9595 3.50543L13.9595 3.50542L13.9573 3.50928L13.8473 3.69928L13.8467 3.70032C13.3372 4.58515 12.6503 4.9998 11.99 4.9998C11.3308 4.9998 10.6449 4.58644 10.1357 3.70443L10.0281 3.50884L10.024 3.50138L10.0196 3.49406C9.6304 2.84013 8.76766 2.60906 8.09931 2.99718C8.0991 2.9973 8.0989 2.99742 8.09869 2.99754L6.37014 3.99671C6.37005 3.99676 6.36995 3.99681 6.36986 3.99687C6.36983 3.99688 6.36981 3.9969 6.36978 3.99691C5.99973 4.21053 5.7125 4.56739 5.59688 5.00097C5.4837 5.42541 5.53902 5.87198 5.7569 6.24966L5.75779 6.25119C6.26579 7.12458 6.28306 7.93916 5.95588 8.51173C5.63137 9.07961 4.9212 9.4698 3.9 9.4698C2.98386 9.4698 2.25 10.2037 2.25 11.1198V12.8798C2.25 13.7831 2.98103 14.5298 3.9 14.5298C4.9212 14.5298 5.63137 14.92 5.95588 15.4879C6.28306 16.0604 6.26579 16.875 5.75779 17.7484L5.75691 17.7499C5.53902 18.1276 5.4837 18.5742 5.59688 18.9986C5.71235 19.4317 5.98753 19.7738 6.34795 19.9956L6.35474 19.9998L6.36166 20.0038L8.08504 20.99C8.41615 21.1917 8.80356 21.2349 9.15373 21.1442C9.53454 21.0483 9.836 20.7985 10.0251 20.493L10.029 20.4867L10.0327 20.4803L10.1422 20.2912C10.1423 20.2911 10.1424 20.2909 10.1424 20.2908C10.6537 19.412 11.3419 18.9898 12 18.9898C12.6603 18.9898 13.3472 19.4044 13.8567 20.2893Z"
            fill="#292D32"
            stroke="white"
          />
        </svg>
      );
    case 'settings_selected':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z"
            fill="#172535"
            stroke="#172535"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
            fill="white"
            stroke="white"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'logout':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.65781 16.553L8.65765 16.5515C8.64448 16.4164 8.74312 16.2915 8.88742 16.2778L8.88977 16.2775C9.01157 16.2653 9.14837 16.3707 9.16229 16.5076C9.31335 18.1406 9.78432 19.3788 10.8052 20.1849C11.8078 20.9766 13.2422 21.27 15.12 21.27H15.25C17.3196 21.27 18.8704 20.9092 19.8848 19.8948C20.8992 18.8804 21.26 17.3296 21.26 15.26V8.74C21.26 6.67037 20.8992 5.11958 19.8848 4.1052C18.8704 3.09082 17.3196 2.73 15.25 2.73H15.12C13.2318 2.73 11.7903 3.02845 10.7867 3.83534C9.76815 4.6543 9.30413 5.91035 9.16275 7.56658C9.13991 7.72647 9.01292 7.81421 8.88742 7.80226L8.88743 7.80212L8.87564 7.80127C8.74625 7.79203 8.63893 7.67634 8.64852 7.52881C8.8121 5.62131 9.40024 4.33021 10.4001 3.50269C11.4112 2.66582 12.9301 2.23 15.11 2.23H15.24C17.6375 2.23 19.23 2.74463 20.2327 3.74731C21.2354 4.74998 21.75 6.34253 21.75 8.74V15.26C21.75 17.6575 21.2354 19.25 20.2327 20.2527C19.23 21.2554 17.6375 21.77 15.24 21.77H15.11C12.9445 21.77 11.4341 21.3414 10.4249 20.5177C9.42686 19.7029 8.83515 18.432 8.65781 16.553Z"
            stroke="white"
          />
          <path
            d="M15 12.25H3.62C3.48614 12.25 3.37 12.1339 3.37 12C3.37 11.8661 3.48614 11.75 3.62 11.75H15C15.1339 11.75 15.25 11.8661 15.25 12C15.25 12.1339 15.1339 12.25 15 12.25Z"
            fill="#292D32"
            stroke="white"
          />
          <path
            d="M5.84994 16.1C5.65994 16.1 5.46994 16.03 5.31994 15.88L1.96994 12.53C1.67994 12.24 1.67994 11.76 1.96994 11.47L5.31994 8.12C5.60994 7.83 6.08994 7.83 6.37994 8.12C6.66994 8.41 6.66994 8.89 6.37994 9.18L3.55994 12L6.37994 14.82C6.66994 15.11 6.66994 15.59 6.37994 15.88C6.23994 16.03 6.03994 16.1 5.84994 16.1Z"
            fill="white"
          />
        </svg>
      );
    default:
      break;
  }
};

export const Puller = styled('div')(() => ({
  width: 30,
  height: 6,
  backgroundColor: '#F3F3F3',
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));
